import { StandardEvents } from '@shopify/events';

// Keeps a custom PDP node in step with the selected variant.
//
// WHY THIS EXISTS. pdp-architecture.md §2 splits the work into "Path A: server
// re-render inside the buy box" and "Path B: client mirror everywhere else".
// Both halves were built on the same wrong assumption: that Horizon hands you a
// re-rendered page. It does not. Horizon fetches ONE section — main-product —
// and every component then pulls its own replacement out of that response.
//
//   Path A failed because a plain Liquid block has no component, so it rendered
//   correctly on first paint and never changed again ("$X each · set of N" stayed
//   on the set-of-4 figure after switching to Pair of 2).
//
//   Path B failed because pitlane-variant-mirror.js searched that same response
//   for the spec sheet's net-weight cell, which is in a DIFFERENT section and is
//   therefore never in it. It found nothing and silently did nothing, forever.
//
// TIMING IS DELIBERATE (RJ, 2026-09-10). An earlier build swapped the unit line
// synchronously from a pre-rendered map, which made it beat Horizon's own price.
// That read as breakage: "they should all be the same or it feels like something's
// wrong, or like clicking the variant didn't work." So this waits on the SAME
// promise product-price.js waits on. Both callbacks drain in one microtask batch,
// so the price and the unit line land together, in one visual change. Do not make
// this faster in isolation — faster than the price is a bug, not an optimisation.
//
//   <pl-variant-sync data-pl-key="unit-price">           in-section
//     <p data-pl-out>                                    the node that gets swapped
//
//   <pl-variant-sync data-pl-key="net-weight" data-pl-section="…">   out-of-section
//
// Without data-pl-section the replacement comes from the response Horizon already
// fetched. With it, that section is fetched, because its markup is not in that
// response — the spec sheet is below the fold and RJ has accepted it trailing.
// Either way Liquid rendered the value; nothing here computes a price or formats a
// weight, per §2's "mirrors never compute independently".

/** Per-URL cache so two nodes in one section cause one fetch, not two. */
const inFlight = new Map();

class PitlaneVariantSync extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    if (!this.section) return;
    // Out-of-section nodes listen on the document: the event is dispatched by the
    // picker inside main-product and bubbles, so it never reaches this element's
    // own section otherwise.
    this.host = this.dataset.plSection ? document : this.section;
    this.host.addEventListener(StandardEvents.productSelect, this.onSelect);
  }

  disconnectedCallback() {
    this.host?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  onSelect = (event) => {
    // Product cards fire the same event; only the PDP's own picker concerns us.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(async ({ detail }) => {
        const key = this.dataset.plKey;
        const out = this.querySelector('[data-pl-out]');
        if (!key || !out || !detail?.html) return;

        const remote = this.dataset.plSection;
        const doc = remote ? await this.#fetchSection(remote, detail.resource?.id) : detail.html;
        if (!doc) return;

        const next = doc.querySelector(`pl-variant-sync[data-pl-key="${key}"] [data-pl-out]`);
        // A missing node means the block is suppressed for the newly selected
        // variant — "$X each" does not apply to a Single. Empty it rather than
        // leave the previous variant's value standing.
        out.innerHTML = next ? next.innerHTML : '';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pl-variant-sync] ' + error);
      });
  };

  /** Renders one section for one variant. Cached per URL across concurrent nodes. */
  async #fetchSection(sectionId, variantId) {
    if (!variantId) return null;
    const url = `${window.location.pathname}?section_id=${encodeURIComponent(
      sectionId
    )}&variant=${encodeURIComponent(variantId)}`;

    if (!inFlight.has(url)) {
      inFlight.set(
        url,
        fetch(url)
          .then((r) => (r.ok ? r.text() : Promise.reject(new Error('section ' + r.status))))
          .then((t) => new DOMParser().parseFromString(t, 'text/html'))
          .finally(() => {
            // Only dedupe concurrent callers; a later switch must fetch again.
            queueMicrotask(() => inFlight.delete(url));
          })
      );
    }
    return inFlight.get(url);
  }
}

if (!customElements.get('pl-variant-sync')) {
  customElements.define('pl-variant-sync', PitlaneVariantSync);
}
